// src\contexts\AlertContext.js
import React, { createContext, useState, useContext, useCallback } from 'react';
import AlertNotification from '../components/common/AlertNotification';

// Création du contexte
const AlertContext = createContext();

// ID pour les alertes
let nextId = 1;

/**
 * Fournisseur du contexte d'alertes
 * Fournit des méthodes pour afficher différents types d'alertes
 */
export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  
  // Fonction pour ajouter une alerte
  const addAlert = useCallback((type, message, duration = 5000) => {
    const id = nextId++;
    setAlerts(prev => [...prev, { id, type, message, duration }]);
    return id;
  }, []);
  
  // Fonction pour supprimer une alerte par son ID
  const removeAlert = useCallback((id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  }, []);
  
  // Méthodes pour différents types d'alertes
  const success = useCallback((message, duration) => {
    return addAlert('success', message, duration);
  }, [addAlert]);
  
  const error = useCallback((message, duration) => {
    return addAlert('error', message, duration);
  }, [addAlert]);
  
  const warning = useCallback((message, duration) => {
    return addAlert('warning', message, duration);
  }, [addAlert]);
  
  const info = useCallback((message, duration) => {
    return addAlert('info', message, duration);
  }, [addAlert]);
  
  // Valeur fournie par le contexte
  const value = {
    alerts,
    addAlert,
    removeAlert,
    success,
    error,
    warning,
    info
  };
  
  return (
    <AlertContext.Provider value={value}>
      {children}
      
      {/* Rendu des alertes actives */}
      <div className="alerts-container">
        {alerts.map((alert, index) => (
          <AlertNotification
            key={alert.id}
            type={alert.type}
            message={alert.message}
            duration={alert.duration}
            onClose={() => removeAlert(alert.id)}
            style={{ bottom: `${(index * 4) + 1}rem` }} // Empiler les alertes
          />
        ))}
      </div>
    </AlertContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte d'alertes
export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert doit être utilisé à l\'intérieur d\'un AlertProvider');
  }
  return context;
};

export default AlertContext;